/**
 * KAN-155 customer opt-out helpers. The settings live on the org preferences
 * doc (`ua_optout_settings`), which the settings store already loads at boot —
 * nothing here reads Firestore. (The one opt-out read of the app is in the
 * message store: a sender's opt-out list, once per Compose send.)
 *
 * Keywords (STOP / START) and the footer text (CTIA's standard "Reply STOP"
 * wording) are fixed, like other messaging tools. The server matches the
 * keywords in functions/sms/optout.js — keep the two lists in sync.
 */
import type { OptOutFooterTarget, OptOutSenderMode, OptOutSettings } from '@/types/settings';
import type { Channel, Conversation, Recipient, Sender } from '@/types/messaging';

/** Replies that opt a customer out (shown in settings; matched on the server).
 *  Only STOP for now — more standard words can be added later. */
export const OPT_OUT_STOP_WORDS = ['STOP'];
/** Replies that opt a customer back in. */
export const OPT_OUT_START_WORDS = ['START'];
/** The footer added to messages — fixed standard wording. */
export const OPT_OUT_FOOTER_TEXT = 'Reply STOP to unsubscribe';
/** The confirmation replies the server sends on a "Lock + reply" sender — fixed,
 *  shown in settings. Same texts as functions/sms/optout.js. */
export const OPT_OUT_CONFIRM_OUT_TEXT = 'You have been unsubscribed. Reply START to subscribe again.';
export const OPT_OUT_CONFIRM_IN_TEXT = 'You are subscribed again. Reply STOP to unsubscribe.';

/** Compose skips a customer who opted out when it sends to several — shown on the skipped recipient. */
export const OPT_OUT_SKIPPED_TEXT = 'Skipped — customer opted out';
/** Compose held back a message to ONE customer who opted out — the result of that send. */
export const OPT_OUT_NOT_SENT_TEXT = 'Not sent — this customer opted out (replied STOP). They can reply START to get messages again.';

/**
 * Where the opt-out list of a sender lives, under UA_INBOX_CONVERSATIONS:
 *   …/UA_INBOX_CONVERSATIONS/UA_OPT_OUT_LISTS/senders/{senderKey}
 *   { numbers: { [customerKey]: time } }
 * Every customer who opted out of ONE sender, in one document, written by the
 * server on each STOP / START (functions/sms/optout.js). Compose reads it once
 * per send, however many recipients.
 */
export const OPT_OUT_LIST_PARENT_ID = 'UA_OPT_OUT_LISTS';
export const OPT_OUT_LIST_COLLECTION = 'senders';

export const OPT_OUT_SENDER_MODES: OptOutSenderMode[] = ['off', 'lock', 'reply'];

export const DEFAULT_OPT_OUT_SETTINGS: OptOutSettings = {
  enabled: false,
  whatsappStopPromotions: false,
  footerOn: { workflow: true, compose: true, inbox: false },
  workflowSendAnyway: false,
  senderModes: {},
};

/** Stored `ua_optout_settings` → a complete settings object (defaults filled in). */
export function normalizeOptOutSettings(raw: unknown): OptOutSettings {
  const r = raw && typeof raw === 'object' ? (raw as Partial<Record<keyof OptOutSettings, unknown>>) : {};
  const footerOn = r.footerOn && typeof r.footerOn === 'object'
    ? (r.footerOn as Partial<Record<OptOutFooterTarget, unknown>>)
    : DEFAULT_OPT_OUT_SETTINGS.footerOn;
  const senderModes: Record<string, OptOutSenderMode> = {};
  if (r.senderModes && typeof r.senderModes === 'object') {
    for (const [key, mode] of Object.entries(r.senderModes as Record<string, unknown>)) {
      if (OPT_OUT_SENDER_MODES.includes(mode as OptOutSenderMode)) senderModes[key] = mode as OptOutSenderMode;
    }
  }
  return {
    enabled: r.enabled === true,
    whatsappStopPromotions: r.whatsappStopPromotions === true,
    footerOn: {
      workflow: footerOn.workflow === true,
      compose: footerOn.compose === true,
      inbox: footerOn.inbox === true,
    },
    workflowSendAnyway: r.workflowSendAnyway === true,
    senderModes,
  };
}

const isWhatsAppChannel = (channel?: Channel | string): boolean =>
  String(channel || '').toUpperCase() === 'WHATSAPP';

/** SMS / MMS — where the provider itself may block a customer who replied STOP. */
export const isOptOutSmsChannel = (channel?: Channel | string): boolean =>
  ['SMS', 'MMS'].includes(String(channel || '').toUpperCase());

/**
 * Warnings shown where an option may not work on SMS. Many SMS providers (Twilio
 * on US / Canada numbers, for one) handle STOP themselves: they send their own
 * "unsubscribed" reply and refuse every later message to that customer (Twilio
 * error 21610) until they reply START. Nothing here detects that — the admin is
 * told, and keeps the choice.
 */
export const OPT_OUT_SMS_WARNINGS = {
  /** Settings — an SMS number set to "Stop messages + confirm". */
  confirm:
    'Heads-up for SMS: many SMS providers (for example Twilio in the US and Canada) already reply to STOP by themselves, and then block all messages to that customer. So our "unsubscribed" message may not be delivered, or the customer may get two of them. For SMS numbers, "Stop messages" is usually the better choice.',
  /** Settings — workflow messages set to "Send anyway". */
  workflow:
    'Works on WhatsApp. On SMS, many providers (for example Twilio in the US and Canada) block all messages to a customer who sent STOP. Those workflow messages will not be delivered, and will show up in Workflow Errors.',
  /** Chat Automation — a rule / flow of an SMS number set to "Send anyway". */
  automation:
    'This is an SMS number. Many SMS providers block all messages to a customer who sent STOP, so this automatic reply may not be delivered.',
  /** Inbox / Compose — "Send anyway?" to one customer on SMS. */
  sendAnyway:
    'This is an SMS. Many SMS providers block all messages to a customer who sent STOP, so this may not be delivered until they reply START.',
} as const;

/**
 * Names one sender in `senderModes`, the same for every provider:
 * "whatsapp_15550001111", "sms_15550001111", "sms_ULGEBRA". Built from the
 * sender's number and channel, so the app and the server agree on it without
 * knowing how each provider writes its numbers ("whatsapp:+1 555…", "+1555…").
 * Same rule as senderKey() in functions/sms/optout.js.
 */
export function optOutSenderKey(number: string | undefined, channel?: Channel | string): string {
  return `${isWhatsAppChannel(channel) ? 'whatsapp' : 'sms'}_${partyName(number)}`;
}

/** "whatsapp:+1 555-000-1111" → "15550001111", "ULGEBRA" → "ULGEBRA". The channel
 *  in front ("whatsapp:", and MessageBird's "sms:") is not part of the name. */
function partyName(value: string | undefined): string {
  return String(value ?? '').trim().replace(/^(whatsapp|sms|mms):/i, '').replace(/[^0-9A-Za-z]+/g, '');
}

/**
 * Names one customer in a sender's opt-out list: their number as the provider
 * writes it, digits only. Same rule as customerKey() in functions/sms/optout.js.
 */
export function optOutCustomerKey(customer: string | undefined): string {
  return partyName(customer);
}

/**
 * Sender and customer of an inbox conversation ("sender::customer"), the way
 * the opt-out list names them — or null when the id has another shape. Same
 * rule as optOutListKeys() in functions/sms/optout.js, so the app looks a
 * customer up under the name the server stored them.
 */
export function optOutListKeys(
  conversationId: string | null | undefined,
  channel?: Channel | string,
): { senderKey: string; customerKey: string } | null {
  const parts = String(conversationId ?? '').split('::');
  if (parts.length !== 2) return null;
  const isWhatsApp = isWhatsAppChannel(channel) || /^whatsapp:/i.test(parts[0].trim());
  const sender = partyName(parts[0]);
  const customerKey = optOutCustomerKey(parts[1]);
  if (!sender || !customerKey) return null;
  return { senderKey: `${isWhatsApp ? 'whatsapp' : 'sms'}_${sender}`, customerKey };
}

/**
 * Sender and customer of a MessageBird message, the way the opt-out list names
 * them — or null when one of them is missing. MessageBird names its
 * conversations itself (a UUID on the old API), so they can't be read back
 * from the conversation id; its list is named after the channel instead:
 * "channel_{channelId}". Same rule as channelListKeys() in functions/sms/optout.js.
 */
export function optOutChannelListKeys(
  channelId: string | undefined,
  customer: string | undefined,
): { senderKey: string; customerKey: string } | null {
  const channel = partyName(channelId);
  const customerKey = optOutCustomerKey(customer);
  if (!channel || !customerKey) return null;
  return { senderKey: `channel_${channel}`, customerKey };
}

/** A stored opt-out list → customer key → the time they opted out. */
export function parseOptOutList(raw: unknown): Map<string, number> {
  const list = new Map<string, number>();
  const numbers = raw && typeof raw === 'object' ? (raw as { numbers?: unknown }).numbers : null;
  if (!numbers || typeof numbers !== 'object') return list;
  for (const [customerKey, time] of Object.entries(numbers as Record<string, unknown>)) {
    if (typeof time === 'number') list.set(customerKey, time);
  }
  return list;
}

/**
 * What a STOP / START does on this sender. Off for every sender while the
 * feature is off. `number` may be a list of names the sender goes by — the
 * first one the admin made a choice for counts.
 */
export function optOutSenderMode(
  settings: OptOutSettings,
  number: string | undefined | Array<string | undefined>,
  channel?: Channel | string,
): OptOutSenderMode {
  if (!settings.enabled) return 'off';
  for (const name of Array.isArray(number) ? number : [number]) {
    const chosen = settings.senderModes[optOutSenderKey(name, channel)];
    if (chosen) return chosen;
  }
  return isWhatsAppChannel(channel) ? 'reply' : 'lock';
}

/**
 * What a Chat Automation rule / flow does with an opted-out customer while it has
 * no choice of its own. WhatsApp answers — the customer wrote first; everything
 * else stays quiet, a STOP there means no more texts. The server uses the same
 * default (_runAutomationConfig).
 */
export function optOutAutomationDefaultPolicy(channel?: Channel | string): 'skip' | 'send' {
  return isWhatsAppChannel(channel) ? 'send' : 'skip';
}

/**
 * Is this conversation locked because the customer opted out? Only while the
 * feature is on AND on for the conversation's own sender (the first half of its
 * id) — switching a sender to "Off" unlocks its chats.
 */
export function isConversationOptedOut(
  conversation: Pick<Conversation, 'optOut' | 'id' | 'channel' | 'metadata'> | null | undefined,
  settings: OptOutSettings,
): boolean {
  if (!settings.enabled || conversation?.optOut?.active !== true) return false;
  // Conversation ids come as "sender::contact" and, from some surfaces, "sender--contact".
  const senderNumber = String(conversation.id || '').split(/::|--/)[0];
  // MessageBird (old API) names a conversation with a UUID — its sender is the channel.
  const channelId = conversation.metadata?.lastUsedChannelId;
  const senderNames = [senderNumber, typeof channelId === 'string' ? channelId : undefined];
  return optOutSenderMode(settings, senderNames, conversation.channel) !== 'off';
}

/**
 * The other way the provider may write this customer's number. WhatsApp (and
 * Twilio) store some countries' mobiles in a different shape than the one typed
 * in the CRM, and the inbox conversation uses the provider's shape:
 *   Mexico    52 + 10 digits   ↔  52 + 1 + 10 digits
 *   Argentina 54 + 10 digits   ↔  54 + 9 + 10 digits
 *   Brazil    55 + area + 8    ↔  55 + area + 9 + 8
 * Returns [] for every other number. Same rule as customerNumberVariants() in
 * functions/sms/optout.js.
 */
export function optOutCustomerNumberVariants(digits: string): string[] {
  const number = String(digits || '');
  if (!/^\d+$/.test(number)) return [];
  const markerAfterCountryCode: Record<string, string> = { '52': '1', '54': '9' };
  const countryCode = number.slice(0, 2);
  const marker = markerAfterCountryCode[countryCode];
  if (marker) {
    if (number.length === 12) return [countryCode + marker + number.slice(2)];
    if (number.length === 13 && number.charAt(2) === marker) return [countryCode + number.slice(3)];
    return [];
  }
  if (countryCode === '55') {
    if (number.length === 12) return [number.slice(0, 4) + '9' + number.slice(4)];
    if (number.length === 13 && number.charAt(4) === '9') return [number.slice(0, 4) + number.slice(5)];
  }
  return [];
}

/**
 * The numbers to look a recipient up under before a Compose send: as typed,
 * plus the other shape where the country has one.
 */
export function optOutRecipientPhones(phone: string): string[] {
  const digits = String(phone || '').replace(/\D/g, '');
  return [phone, ...optOutCustomerNumberVariants(digits).map((variant) => `+${variant}`)];
}

/**
 * Names one opt-out on one chat. "Send anyway" is remembered against this key,
 * so it unlocks that chat only, and a newer STOP (a new time) locks it again.
 */
export function optOutLockKey(conversation: Pick<Conversation, 'id' | 'optOut'>): string {
  return `${conversation.id}|${conversation.optOut?.time ?? ''}`;
}

/**
 * Names one Compose message to ONE customer: sender, channel and the customer's
 * number. "Send anyway" is remembered against this key, so it only counts for
 * the customer the agent was asked about. Null for several recipients — there
 * the customers who opted out are skipped, without a "Send anyway".
 */
export function optOutComposeKey(
  sender: Pick<Sender, 'id'> | null | undefined,
  channel: Channel,
  recipients: Array<Pick<Recipient, 'phone'>>,
): string | null {
  if (!sender || recipients.length !== 1) return null;
  return [sender.id, channel, recipients[0].phone].join('|');
}

/**
 * The footer as it is sent. A message has one text size, so the footer can't be
 * made smaller for the customer — it is set apart instead: a blank line above
 * it, and italics on WhatsApp (`_text_`). SMS has no styles and would show the
 * underscores, so it stays plain. Same rule on the server (functions/sms/optout.js).
 */
function optOutFooterFor(channel?: Channel): string {
  return isWhatsAppChannel(channel) ? `_${OPT_OUT_FOOTER_TEXT}_` : OPT_OUT_FOOTER_TEXT;
}

/** Adds the opt-out footer to a plain-text message when settings ask for it here. */
export function appendOptOutFooter(
  body: string,
  settings: OptOutSettings,
  where: OptOutFooterTarget,
  channel?: Channel,
): string {
  if (!settings.enabled || !settings.footerOn[where]) return body;
  if (!body.trim() || body.includes(OPT_OUT_FOOTER_TEXT)) return body;
  return `${body}\n\n${optOutFooterFor(channel)}`;
}

/**
 * For senders that append a signature line: the signature with the opt-out
 * footer added after it (or the footer alone), so the footer lands exactly
 * where the signature does. `body` is checked so a footer typed by hand isn't
 * added twice. An approved provider template gets the signature only: its text
 * is fixed, so the footer would never reach the customer — it would only show
 * up in the saved copy of the message.
 */
export function signatureWithOptOutFooter(
  signature: string | undefined,
  settings: OptOutSettings,
  where: OptOutFooterTarget,
  message: { body?: string; channel?: Channel; isApprovedTemplate?: boolean } = {},
): string | undefined {
  const { body = '', channel, isApprovedTemplate = false } = message;
  const sig = signature?.trim() || '';
  if (isApprovedTemplate || !settings.enabled || !settings.footerOn[where]) return sig || undefined;
  if (sig.includes(OPT_OUT_FOOTER_TEXT) || body.includes(OPT_OUT_FOOTER_TEXT)) return sig || undefined;
  const footer = optOutFooterFor(channel);
  if (sig) return `${sig}\n\n${footer}`;
  // The senders join message and signature with one line break; the extra one
  // here is the blank line above the footer.
  return body.trim() ? `\n${footer}` : footer;
}

const OPT_OUT_FOOTER_AT_END = new RegExp(
  `(?:^|\\n)\\s*_?${OPT_OUT_FOOTER_TEXT.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_?\\s*$`,
);

/**
 * A sent message → its own text and the opt-out footer at its end (null when
 * it has none), so the inbox can show the footer as a quiet second line. The
 * italic marks are left out: the inbox styles the footer itself.
 */
export function splitOptOutFooter(body: string): { text: string; footer: string | null } {
  const match = OPT_OUT_FOOTER_AT_END.exec(body);
  if (!match) return { text: body, footer: null };
  return { text: body.slice(0, match.index).replace(/\s+$/, ''), footer: OPT_OUT_FOOTER_TEXT };
}
